<template>
    <div class="status-block-wrapper" @click.stop>
        <div class="status-block">
            <app-tooltip placement="top-start">
                <div slot="content">
                    {{ title }}
                    <template v-if="user">by {{ user }}</template>
                </div>
                <div
                    :aria-label="status.title"
                    :class="[
                        status ? 'status--' + status : '',
                        overrideStatus ? 'status--override-' + overrideStatus : '',
                        {
                            'is-override': attendance.lock,
                            'meets': attendance.meets,
                            'is-issue': issue,
                            'is-pending': awaiting
                        }
                    ]"
                    role="button"
                    tabindex="0"
                    class="status-block-inner"
                    @contextmenu.stop.prevent="handleContextOpen"
                    @click.stop="selected"
                    @keyup.enter="selected">
                    <div v-if="awaiting" class="loading"/>
                </div>
            </app-tooltip>

            <div v-if="hasPindrop" class="status-badge"/>
        </div>
        <div v-if="size == 'large'" class="status-block-text">{{ title }}</div>
        <status-menu ref="statusMenu" v-model="attendance"/>
    </div>
</template>

<script>
import StatusMenu from './StatusMenu.vue'
import { first, last, get, nameFormat } from '../../utilities'

export default {
    name: 'status-block',
    components: {
        StatusMenu
    },
    props: {
        attendance: {
            type: Object,
            default: function() {
                return { status: 7 }
            }
        },
        size: {
            type: String,
            default: 'small'
        }
    },
    data() {
        return {
            showOverride: false
        }
    },
    computed: {
        issue() {
            if (this.attendance.lock && this.attendance.status == 2) {
                return false
            } 
            else {
                return (
                    this.attendance.status === 3 ||
                    (this.attendance.enter !== null && this.attendance.status == 2)
                )
            }
        },
        lastOverride() {
            return last(this.attendance.overrides) || null
        },
        firstOverride() {
            return first(this.attendance.overrides) || null
        },
        status() {
            return this.statusFromValue(this.attendance.status)
        },
        overrideStatus() {
            const override = this.firstOverride
            if (override === null) {
                return null
            }
            return this.statusFromValue(override.old_status)
        },
        title() {
            switch (this.attendance.status) {
            case 0:
                if (this.attendance.lock || this.attendance.enter) {
                    return 'Present'
                }
                return 'No Activity Yet'
            case 1:
                return 'Tardy'
            case 2:
                return 'Absent'
            case 3:
                return 'Issue'
            case 4:
                return 'Travel'
            case 5:
                return 'Cancelled'
            case 6:
                return 'Excused'
            case 7:
                return 'Not Active'
            case 8: 
                return 'Hybrid'
            }
            if (this.attendance.meets) {
                return 'Class Meets'
            }
            return 'No Class'
        },
        message() {
            return this.title + ' by ' + this.user
        },
        user() {
            const user = get(this.lastOverride, 'user', {})
            return nameFormat(user)
        },
        awaiting() {
            return (
                this.attendance.status == 0 &&
                !(
                    this.attendance.lock ||
                    this.attendance.enter ||
                    this.attendance.finished
                )
            )
        },
        hasPindrop() {
            return get(this, 'attendance.coordinates.length', 0) > 0
        }
    },
    methods: {
        selected(event) {
            this.$emit('selected', event)
        },
        handleContextOpen() {
            if (this.attendance && this.attendance.status != null) {
                this.$refs.statusMenu.open()
            }
        },
        statusFromValue(value) {
            switch (value) {
            case 0:
                return 'present'
            case 1:
                return 'tardy'
            case 2:
                return 'absent'
            case 3:
                return 'issue'
            case 4:
            case 5:
            case 6:
            case 8:
                return 'excused'
            default:
                return 'scheduled'
            }
        }
    }
}
</script>

<style lang="stylus">
@import '../../stylus/variables'

.status-block
    width: 25px
    height: 29px
    border-radius: 6px
    display: inline-block
    padding: 2px 0px
    position: relative

    .status-block-inner
        width: 25px
        height: 25px
        background-color: $light-grey
        border-bottom: 4px solid transparent
        border-radius: 6px
        color: #FFF
        font-size: 20px
        text-align: center
        line-height: 22px
        position: relative

        &.meets
            background: $medium-grey

        &.is-override
            border-radius: 6px

        &.status--present:not(.is-pending)
            background-color: $status-present

        &.status--tardy
            background-color: $status-tardy

        &.status--absent
            border-color: $status-absent
            background-color: $status-absent

        &.status--issue
            background-color: $status-issue

        &.status--excused
            background-color: $status-excused

        &.status--override-scheduled
            border-bottom-color: $border-color

        &.status--override-present
            border-bottom-color: $status-present

        &.status--override-tardy
            border-bottom-color: $status-tardy

        &.status--override-absent
            border-bottom-color: $status-absent

        &.status--override-issue
            border-bottom-color: $status-issue

        &.status--override-excused
            border-bottom-color: $status-excused

        &.status--override-meets
            border-color: $medium-grey

        &.is-issue::after
            position: absolute
            left: 0px
            width: 100%
            content: '?'
            font-size: 14px
            font-weight: bold

.status-block-wrapper
    display: inline-flex

    .status-block-text
        margin-left: 15px
        padding: 5px 0

.status-badge
    display: block
    background: $green
    width: 10px
    height: 10px
    position: absolute
    right: -3px
    top: -1px
    border: 1px solid $white
    border-radius: 5px

.loading
    width: 23px
    height: 23px
    margin: 0px auto
    background-color: rgba(255, 255, 255, 0.6)
    border-radius: 100%
    -webkit-animation: sk-scaleout 2s infinite ease-in-out
    animation: sk-scaleout 2s infinite ease-in-out

    @keyframes sk-scaleout
        0%
            transform: scale(0)

        100%
            transform: scale(1)
            opacity: 0
</style>
